import { useFormik } from "formik";
import {useEffect, useMemo, useState} from "react";
import axios from "axios";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";

import useUserData from "../user/useUserData";
import useCartData from "../cart/useCartData";
import { handleAxiosErrors } from "../../helpers/errorHandling";
import { API } from "../../helpers/constants";
import { PRODUCT_INFO } from "../../locales/components/namespaces";
import { buildOrderStoreFormData } from "../../helpers/orders/buildOrderStoreFormData";
import {useHistory} from "react-router-dom";
import {getRouteUrl} from "../../helpers/routing/getRouteUrl";
import {ROUTE_ADDRESS_CREATE, ROUTE_THANK_YOU} from "../../routers/routes";

export const useCheckout = ({ onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const { token } = useUserData();
    const { t } = useTranslation(PRODUCT_INFO);
    const [currentPayment, setCurrentPayment] = useState(null);
    const {
        addresses = [],
        carriers = [],
        ciCarriers = [],
        countries = [],
        sum = 0,
        vat = 0,
    } = useCartData();

    const validationSchema = useMemo(() => {
        return yup.object().shape({
            addressId: yup.string().when("addNewAddress", {
                is: false,
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            fullName: yup.string().when("addNewAddress", {
                is: true,
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            street: yup.string().when("addNewAddress", {
                is: true,
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            postalCode: yup.string().when("addNewAddress", {
                is: true,
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            city: yup.string().when("addNewAddress", {
                is: true,
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            phone: yup.string().when("addNewAddress", {
                is: true,
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            email: yup.string().when("addNewAddress", {
                is: true,
                then: (schema) => schema.email(t("Invalid email")).required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            country: yup.string().when("addNewAddress", {
                is: true,
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            deliveryMethod: yup.string().required(t("Required")),
            paymentMethod: yup.string().required(t("Required")),

            partneredCarrierId: yup.string().when("deliveryMethod", {
                is: "partnered",
                then: (schema) => schema.required(t("Required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            recipientCarrierId: yup.string().when(
                ["deliveryMethod", "recipientCarrierMode"],
                {
                    is: (deliveryMethod, recipientCarrierMode) =>
                        deliveryMethod === "recipient_transport" &&
                        recipientCarrierMode === "saved",
                    then: (schema) => schema.required(t("Required")),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),

            recipientCarrierMode: yup.string().notRequired(),

            recipientCarrierForm: yup.object().shape({
                companyName: yup.string().test(
                    "companyName-required-when-new-carrier",
                    t("Required"),
                    function (value) {
                        const root = this.from?.[1]?.value;
                        if (
                            root?.deliveryMethod === "recipient_transport" &&
                            root?.recipientCarrierMode === "new"
                        ) {
                            return !!value?.trim();
                        }
                        return true;
                    }
                ),
                contactPerson: yup.string().notRequired(),
                pickupDate: yup.string().notRequired(),
                phone: yup.string().test(
                    "phone-required-when-new-carrier",
                    t("Required"),
                    function (value) {
                        const root = this.from?.[1]?.value;
                        if (
                            root?.deliveryMethod === "recipient_transport" &&
                            root?.recipientCarrierMode === "new"
                        ) {
                            return !!value?.trim();
                        }
                        return true;
                    }
                ),
                email: yup.string().notRequired(),
                address: yup.string().notRequired(),
                city: yup.string().notRequired(),
                zipCode: yup.string().notRequired(),
            }),

            acceptTerms: yup
                .boolean()
                .oneOf([true], t("You must accept the terms")),
        });
    }, [t]);


const onSubmit = (values) => {

    setLoading(true);

    const subtotalWithoutVat = Number(sum || 0) - Number(vat || 0);
    const totalWithVat = Number(sum || 0);

    const formData = buildOrderStoreFormData({
        values,
        subtotalWithoutVat,
        totalWithVat,
    });

    axios({
        method: "post",
        data: formData,
        headers: {
            Authorization: `Bearer ${token}`,
        },
        url: `${API}/en/brew/order/store`,
    }).then((res) => {



    if (typeof onSuccess === "function") {
        onSuccess(res.data);
    }

    if (res.data.data.payment && res.data.data.data) {

        setCurrentPayment({
            status: res.data.data.data.Status,
            transactionTicket: res.data.data.data.TransactionTicket,
            resultCode: res.data.data.data.ResultCode,
            resultDescription: res.data.data.data.ResultDescription,
            username: res.data.data.data.Username,
            posId: res.data.data.data.PosId,
            acquirerId: res.data.data.data.AcquirerId,
            merchantId: res.data.data.data.MerchantId,
            merchantReference: res.data.data.data.MerchantReference,
            addressId: res.data.data.data.AddressId,
            minutesToExpiration: res.data.data.data.MinutesToExpiration,
            timestamp: res.data.data.data.Timestamp,
            paymentId: res.data.data.data.PaymentId,
        });

    } else {

        enqueueSnackbar(t("Order was successfully submitted!"), {
            variant: "success",
            autoHideDuration: 3000,
        });

        setLoading(false);

        history.push({
            pathname: getRouteUrl(ROUTE_THANK_YOU),
            state: { response: res.data },
        });
    }
})
        .catch((error) => {

            setLoading(false);

            enqueueSnackbar(
                handleAxiosErrors(
                    error,
                    t("An error occurred!")
                ),
                {
                    variant: "error",
                    autoHideDuration: 3000,
                }
            );
        });
};


    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            addressId: addresses?.[0]?.id || "",
            addNewAddress: false,

            deliveryName: addresses?.[0]?.name || "",
            fullName:  "",
            street: addresses?.[0]?.Address || "",
            address2:  "",
            postalCode: addresses?.[0]?.ZipCode || "",
            city: addresses?.[0]?.City || "",
            company:  "",
            phone: addresses?.[0]?.AddrPhone || "",
            email: addresses?.[0]?.email || "",
            country: addresses?.[0]?.CountryId || "",

            comment: "",

            deliveryMethod: "partnered",
            partneredCarrierId: ciCarriers?.[0]?.id || "",

            recipientCarrierMode: "saved",
            recipientCarrierId: carriers?.[0]?.id || "",
            saveRecipientCarrier: false,
            acceptRecipientTerms: false,

            recipientCarrierForm: {
                companyName: "",
                contactPerson: "",
                pickupDate: "",
                phone: "",
                email: "",
                address: "",
                city: "",
                zipCode: "",
            },

            paymentMethod: "cod",
            acceptTerms: false,
        },
        validationSchema,
        onSubmit,
    });

    const partneredCarrierOptions = ciCarriers.map((carrier) => ({
        value: carrier.id,
        label: carrier.name,
    }));

    const recipientCarrierOptions = carriers.map((carrier) => ({
        value: carrier.id,
        label: carrier.name,
    }));
    const countryOptions = countries.map((country) => ({
        id: country.id,
        Description: country.Description || country.name,
    }));

    // useEffect(() => {
    //
    // }, [formik.errors, formik.values, formik.isValid, formik.submitCount]);

    return {
        formik,
        loading,
        addresses,
        currentPayment,
        countries,
        partneredCarrierOptions,
        recipientCarrierOptions,
    };
};